import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SocialMediaAnalysisPanel = ({ lead, onAnalyze }) => {
  const socialPlatforms = [
    { name: 'Facebook', icon: 'Facebook', followers: lead?.socialMedia?.facebook?.followers, engagement: lead?.socialMedia?.facebook?.engagement, postsPerWeek: lead?.socialMedia?.facebook?.postsPerWeek },
    { name: 'Instagram', icon: 'Instagram', followers: lead?.socialMedia?.instagram?.followers, engagement: lead?.socialMedia?.instagram?.engagement, postsPerWeek: lead?.socialMedia?.instagram?.postsPerWeek },
    { name: 'Twitter', icon: 'Twitter', followers: lead?.socialMedia?.twitter?.followers, engagement: lead?.socialMedia?.twitter?.engagement, postsPerWeek: lead?.socialMedia?.twitter?.postsPerWeek },
    { name: 'LinkedIn', icon: 'Linkedin', followers: lead?.socialMedia?.linkedin?.followers, engagement: lead?.socialMedia?.linkedin?.engagement, postsPerWeek: lead?.socialMedia?.linkedin?.postsPerWeek },
    { name: 'YouTube', icon: 'Youtube', followers: lead?.socialMedia?.youtube?.subscribers, engagement: lead?.socialMedia?.youtube?.engagement, postsPerWeek: lead?.socialMedia?.youtube?.videosPerWeek },
    { name: 'TikTok', icon: 'Video', followers: lead?.socialMedia?.tiktok?.followers, engagement: lead?.socialMedia?.tiktok?.engagement, postsPerWeek: lead?.socialMedia?.tiktok?.postsPerWeek }
  ];

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1000000) return `${(num / 1000000)?.toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000)?.toFixed(1)}K`;
    return num?.toString();
  };

  const getEngagementColor = (rate) => {
    if (!rate) return 'text-gray-500';
    if (rate >= 5) return 'text-green-600';
    if (rate >= 2) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEngagementLabel = (rate) => {
    if (!rate) return 'Unknown';
    if (rate >= 5) return 'High';
    if (rate >= 2) return 'Medium';
    return 'Low';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Icon name="Share2" size={24} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Social Media Analysis</h3>
            <p className="text-sm text-muted-foreground">Comprehensive social presence overview</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          onClick={() => onAnalyze?.(lead)}
        >
          Refresh Analysis
        </Button>
      </div>

      {/* Overall Social Score */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Overall Social Media Score</p>
            <p className="text-3xl font-bold text-foreground">
              {lead?.socialMedia?.overallScore || 'N/A'}/100
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground mb-1">Total Reach</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(lead?.socialMedia?.totalReach)}
            </p>
          </div>
        </div>
      </div>

      {/* Platform Analysis Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {socialPlatforms?.map((platform) => (
          <div
            key={platform?.name}
            className="bg-muted border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name={platform?.icon} size={20} className="text-primary" />
                <h4 className="font-semibold text-foreground">{platform?.name}</h4>
              </div>
              {platform?.followers && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Followers</span>
                <span className="text-sm font-medium text-foreground">
                  {formatNumber(platform?.followers)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Engagement</span>
                <span className={`text-sm font-medium ${getEngagementColor(platform?.engagement)}`}>
                  {platform?.engagement ? `${platform?.engagement}%` : 'N/A'}
                  {platform?.engagement && (
                    <span className="ml-1 text-xs">({getEngagementLabel(platform?.engagement)})</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Activity</span>
                <span className="text-sm font-medium text-foreground">
                  {platform?.postsPerWeek || 'N/A'} posts/week
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Insights and Recommendations */}
      <div className="border-t border-border pt-4">
        <h4 className="font-semibold text-foreground mb-3 flex items-center">
          <Icon name="Lightbulb" size={16} className="mr-2 text-warning" />
          Key Insights
        </h4>
        <div className="space-y-2">
          {lead?.socialMedia?.insights?.map((insight, index) => (
            <div key={index} className="flex items-start space-x-2 p-2 bg-muted rounded">
              <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
              <p className="text-sm text-foreground">{insight}</p>
            </div>
          )) || (
            <p className="text-sm text-muted-foreground italic">
              No insights available yet. Run analysis to generate recommendations.
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center space-x-3 mt-6 pt-4 border-t border-border">
        <Button variant="primary" size="sm" iconName="MessageCircle" className="flex-1">
          Contact via Social
        </Button>
        <Button variant="outline" size="sm" iconName="BarChart3" className="flex-1">
          View Detailed Report
        </Button>
      </div>
    </div>
  );
};

export default SocialMediaAnalysisPanel;